import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Package, ArrowRight, Calendar } from "lucide-react";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { PageBreadcrumb } from "@/components/page-breadcrumb";

interface ActionCardPack {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  status: string;
  tags: string[] | null;
  coverImage: string | null;
  publishedAt: string | null;
}

export default function ActionCardsPage() {
  const [, navigate] = useLocation();

  const { data: packs, isLoading } = useQuery<ActionCardPack[]>({
    queryKey: ["/api/public/action-card-packs"],
  });

  return (
    <div className="min-h-screen flex flex-col">
      <SiteHeader />
      <div className="container mx-auto px-4 pt-4">
        <PageBreadcrumb items={[{ label: "Products" }]} />
      </div>
      <main className="flex-1 bg-background">
        <div className="bg-muted/30 border-b">
          <div className="container mx-auto px-4 py-12">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold mb-2">Product Collections</h1>
                <p className="text-muted-foreground max-w-2xl">
                  Browse our curated collections of industrial products. Save items to your short list 
                  to request introductions and technical documentation.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4 py-8">
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <Card key={i}>
                  <CardHeader>
                    <Skeleton className="h-6 w-3/4" />
                    <Skeleton className="h-4 w-1/2" />
                  </CardHeader>
                  <CardContent>
                    <Skeleton className="h-32 w-full mb-4" />
                    <Skeleton className="h-4 w-full" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : packs && packs.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {packs.map((pack) => (
                <Card 
                  key={pack.id} 
                  className="hover-elevate cursor-pointer group"
                  onClick={() => navigate(`/products/${pack.slug}`)}
                  data-testid={`card-pack-${pack.id}`}
                >
                  {pack.coverImage ? (
                    <div className="h-40 overflow-hidden rounded-t-lg">
                      <img
                        src={pack.coverImage}
                        alt={pack.name}
                        className="w-full h-full object-cover transition-transform group-hover:scale-105"
                      />
                    </div>
                  ) : (
                    <div className="h-40 bg-muted/50 flex items-center justify-center rounded-t-lg">
                      <Package className="h-12 w-12 text-muted-foreground/50" />
                    </div>
                  )}
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      {pack.name}
                    </CardTitle>
                    {pack.publishedAt && (
                      <div className="flex items-center gap-1 text-sm text-muted-foreground">
                        <Calendar className="h-3 w-3" />
                        {new Date(pack.publishedAt).toLocaleDateString()}
                      </div>
                    )}
                  </CardHeader>
                  <CardContent>
                    {pack.description && (
                      <CardDescription className="mb-4 line-clamp-2">
                        {pack.description}
                      </CardDescription>
                    )}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {pack.tags?.slice(0, 4).map((tag) => (
                        <Badge key={tag} variant="secondary" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                    <Button variant="ghost" className="w-full justify-between group">
                      View Products
                      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <Package className="h-16 w-16 mx-auto mb-4 text-muted-foreground/50" />
              <h2 className="text-xl font-semibold mb-2">No Collections Available</h2>
              <p className="text-muted-foreground">
                Check back soon for curated product collections.
              </p>
            </div>
          )}
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
